/**
 * The sync code: 16 Crockford base32 characters (80 random bits), shown as XXXX-XXXX-XXXX-XXXX. It is the one secret
 * that ties devices together: everything the server needs (an id, a write proof, the encryption key) is derived from it
 * on the device, and the code itself never leaves the device except inside a link's #fragment, which browsers do not send.
 */
const ALPHABET = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";
export const SYNC_CODE_LENGTH = 16;

export function makeSyncCode(random: (n: number) => Uint8Array = (n) => crypto.getRandomValues(new Uint8Array(n))): string {
  // 10 bytes = 80 bits = 16 base32 characters, encoded properly so every bit of randomness is used.
  const bytes = random(10);
  let bits = 0;
  let acc = 0;
  let out = "";
  for (const b of bytes) {
    acc = (acc << 8) | b;
    bits += 8;
    while (bits >= 5) {
      out += ALPHABET[(acc >>> (bits - 5)) & 31];
      bits -= 5;
    }
  }
  return out;
}

/** Normalise what a person typed or pasted: case, dashes, spaces, and Crockford's forgiving look-alikes (O→0, I/L→1). Null if it isn't a code. */
export function parseSyncCode(input: string): string | null {
  const s = input.toUpperCase().replace(/[\s-]/g, "").replace(/O/g, "0").replace(/[IL]/g, "1");
  return s.length === SYNC_CODE_LENGTH && [...s].every((ch) => ALPHABET.includes(ch)) ? s : null;
}

export const formatSyncCode = (code: string): string => code.match(/.{1,4}/g)?.join("-") ?? code;

/** The link to open on the other device. The code rides in the fragment, so it is never part of any request. */
export const syncLink = (origin: string, code: string): string => `${origin}/sync#${code}`;

/** The code from a /sync link's fragment, or null. */
export const codeFromFragment = (hash: string): string | null => parseSyncCode(hash.replace(/^#/, ""));
